import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { environmentIsDevelop, formatCurrencyLong } from "../../../common/utils"
import { SUBSCRIPTION_PLANS } from "../../users/billing"
import { selectSubscription } from "../../users/reducer"
import { PLAN_BILLING_SECTIONS, SETTINGS_TABS } from "../settings"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

export default function CheckoutDrawer({ open, onClose, initialPlan }) {
  const [selectedPlan, setSelectedPlan] = useState(SUBSCRIPTION_PLANS[0])
  const [promoCode, setPromoCode] = useState("")
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState(false)
  const [stripeClientSecret, setStripeClientSecret] = useState(null)
  const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(false)
  const [stripeCoupon, setStripeCoupon] = useState(null)
  const [promoCodeError, setPromoCodeError] = useState("")
  const [payButtonClicked, setPayButtonClicked] = useState(false)
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  const zohoScriptRef = useRef(null)

  const axios = useAuthorizedAxiosClient()
  const Container = useDialogContainer()
  const subscription = useSelector(selectSubscription)

  function handleApplyCouponButtonClicked() {
    setIsValidatingPromoCode(true)
    axios
      .get(`/billing/api/validate-promo-code/?code=${promoCode}`)
      .then(({ data }) => {
        setStripeCoupon(data.coupon)
        setPromoCodeError(null)
      })
      .catch(() => {
        setStripeCoupon(null)
        setPromoCodeError("Invalid promotional code")
      })
      .finally(() => {
        setIsValidatingPromoCode(false)
      })
  }

  function handlePromoCodeChanged(e) {
    setPromoCode(e.target.value)
    setPromoCodeError(null)
  }

  function handleRemoveCoupon() {
    setStripeCoupon(null)
    setPromoCode("")
    setPromoCodeError("")
  }

  function getDiscountAmount() {
    if (stripeCoupon) {
      if (stripeCoupon.amount_off) {
        return stripeCoupon.amount_off / 100
      }
      if (stripeCoupon.percent_off) {
        return (selectedPlan.price * stripeCoupon.percent_off) / 100
      }
    }
    return 0
  }

  function getDiscountedPrice() {
    return selectedPlan.price - getDiscountAmount()
  }

  useEffect(() => {
    if (initialPlan) {
      setSelectedPlan(initialPlan)
    }
  }, [initialPlan])

  useEffect(() => {
    if (!environmentIsDevelop()) return
    if (!open) return
    if (zohoScriptRef.current) return
    // Script must load AFTER the widget div is in the DOM,
    // so we wait one tick for React to paint the drawer.
    const timer = setTimeout(() => {
      const script = document.createElement("script")
      script.src = "/js/zf-widget.js"
      script.async = true
      zohoScriptRef.current = script
      document.body.appendChild(script)
    }, 100)
    return () => clearTimeout(timer)
  }, [open])

  useEffect(
    () => {
      if (!open) {
        return
      }

      setIsLoadingClientSecret(true)
      const urlSearchParams = new URLSearchParams()
      urlSearchParams.append("subscription_id", subscription.id)
      urlSearchParams.append("plan", selectedPlan.id)
      if (stripeCoupon) {
        urlSearchParams.append("coupon_id", stripeCoupon.id)
      }
      axios
        .get(`/billing/api/stripe-client-secret/?${urlSearchParams.toString()}`)
        .then(({ data }) => setStripeClientSecret(data.client_secret))
        .finally(() => setIsLoadingClientSecret(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, selectedPlan, stripeCoupon]
  )

  return (
    <Container
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiPaper-root": {
          width: { xs: "100%", sm: "520px" },
          borderRadius: { xs: "24px 24px 0 0", sm: 0 },
        },
      }}
    >
      <DialogHeader title="Checkout" onClose={onClose} />
      <ContainerColumn>
        <Column>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Select a plan
          </Typography>
          <Column sx={{ gap: 1.5 }}>
            {SUBSCRIPTION_PLANS.map((plan) => {
              const isSelected = plan.id === selectedPlan.id
              const isActive = subscription.isPlanActive(plan)
              return (
                <PlanCard
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  isSelected={isSelected}
                >
                  {isActive && (
                    <ActivePlanChip label="Already on" size="small" />
                  )}
                  <Row
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Column>
                      <Typography sx={{ fontWeight: 600 }}>
                        {plan.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: (theme) => theme.palette.colors.gray600,
                        }}
                      >
                        {plan.billingCycle}
                      </Typography>
                    </Column>
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatCurrencyLong(plan.price)}
                    </Typography>
                  </Row>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: (theme) => theme.palette.colors.gray600,
                    }}
                  >
                    {plan.description}
                  </Typography>
                </PlanCard>
              )
            })}
          </Column>
        </Column>
        <CouponColumn
          component="form"
          onSubmit={handleApplyCouponButtonClicked}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Coupon
          </Typography>
          <Row sx={{ gap: 1 }}>
            <TextField
              size="small"
              placeholder="Enter coupon"
              value={promoCode}
              onChange={handlePromoCodeChanged}
              error={!!promoCodeError}
              helperText={promoCodeError}
              fullWidth
            />
            <ApplyCouponButton
              variant="outlined"
              type="submit"
              size="small"
              loading={isValidatingPromoCode}
              disabled={!promoCode}
              onClick={handleApplyCouponButtonClicked}
            >
              Apply
            </ApplyCouponButton>
          </Row>
          {stripeCoupon && (
            <RemoveCouponChip
              label={stripeCoupon.name}
              size="small"
              onDelete={handleRemoveCoupon}
            />
          )}
        </CouponColumn>
        <BreakdownColumn>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Payment breakdown
          </Typography>
          <Row sx={{ justifyContent: "space-between" }}>
            <Typography color="text.secondary">Subtotal</Typography>
            <Typography>{formatCurrencyLong(selectedPlan.price)}</Typography>
          </Row>
          <Row sx={{ justifyContent: "space-between" }}>
            <Typography color="text.secondary">Discount</Typography>
            <Typography>-{formatCurrencyLong(getDiscountAmount())}</Typography>
          </Row>
          <Divider />
          <Row
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Total
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatCurrencyLong(getDiscountedPrice())}
            </Typography>
          </Row>
        </BreakdownColumn>
        {stripeClientSecret && !isLoadingClientSecret && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: stripeClientSecret }}
          >
            <StripeForm
              payButtonClicked={payButtonClicked}
              setPayButtonClicked={setPayButtonClicked}
              setIsConfirmingPayment={setIsConfirmingPayment}
              selectedPlan={selectedPlan}
            />
          </Elements>
        )}
        {environmentIsDevelop() && (
          <div
            id="zf-widget-root-id-d9dpuzuf3"
            data-pricing-table="true"
            data-digest="2-9e173bc9e96e0b241856eb169062c51bb17b271e6f6051dcd4852a57c43215c2698ccd96077e6e531781cdff4b1a5e5daf7fa849bc53893cfc403215b90c4efd"
            data-product_url="https://billing.zohocloud.ca"
          />
        )}
      </ContainerColumn>
      <Column
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.colors.gray200}`,
          p: { xs: 2, sm: 3 },
          gap: 1,
        }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          loading={isLoadingClientSecret || isConfirmingPayment}
          onClick={() => setPayButtonClicked(true)}
          sx={{
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          Confirm & Pay {formatCurrencyLong(getDiscountedPrice())}
        </LoadingButton>
        <Typography
          variant="caption"
          sx={{
            color: (theme) => theme.palette.colors.gray600,
          }}
        >
          Secured payment • You will be charged immediately
        </Typography>
      </Column>
    </Container>
  )
}

function StripeForm({
  payButtonClicked,
  setPayButtonClicked,
  setIsConfirmingPayment,
  selectedPlan,
}) {
  const [stripeErrorMessage, setStripeErrorMessage] = useState(null)
  const elements = useElements()
  const stripe = useStripe()

  useEffect(
    () => {
      if (payButtonClicked) {
        setPayButtonClicked(false)
        setStripeErrorMessage(null)
        setIsConfirmingPayment(true)
        const redirectSearchParams = new URLSearchParams()
        redirectSearchParams.append("plan", selectedPlan.id)
        redirectSearchParams.append("tab", SETTINGS_TABS.planBilling)
        redirectSearchParams.append(
          "section",
          PLAN_BILLING_SECTIONS.subscription
        )
        const redirectURL = new URL(
          `/settings/?${redirectSearchParams.toString()}`,
          window.location.href
        ).toString()
        stripe
          .confirmSetup({
            elements,
            confirmParams: {
              return_url: redirectURL,
            },
          })
          .then(({ error }) => {
            if (error) {
              setStripeErrorMessage(error.message)
            }
          })
          .finally(() => setIsConfirmingPayment(false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payButtonClicked]
  )

  return (
    <Column sx={{ gap: 1 }}>
      {stripeErrorMessage && (
        <Typography sx={{ fontWeight: 500, color: "error.main" }}>
          {stripeErrorMessage}
        </Typography>
      )}
      <PaymentElement />
    </Column>
  )
}

const ContainerColumn = styled(Column)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  [theme.breakpoints.only("xs")]: {
    paddingInline: theme.spacing(2),
  },
}))

const PlanCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ theme, isSelected }) => ({
  border: `2px solid ${
    isSelected ? theme.palette.colors.brand500 : theme.palette.colors.gray200
  }`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  cursor: "pointer",
  backgroundColor: isSelected
    ? theme.palette.colors.brand50
    : theme.palette.background.paper,
  position: "relative",
}))

const ActivePlanChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: -12,
  right: 16,
  backgroundColor: theme.palette.colors.success600,
  color: "white",
  fontWeight: 700,
  fontSize: "0.75rem",
  height: "26px",
  boxShadow: `0 4px 12px ${theme.palette.colors.success600}40`,
  border: `1.5px solid ${theme.palette.colors.success700}`,
  "& .MuiChip-label": {
    padding: theme.spacing(0.3, 1.8),
  },
}))

const CouponColumn = styled(Column)(({ theme }) => ({
  border: `1px dashed ${theme.palette.colors.gray300}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  gap: theme.spacing(1.5),
}))

const ApplyCouponButton = styled(LoadingButton)(({ theme }) => ({
  minWidth: "80px",
  height: "40px",
  textTransform: "none",
  fontWeight: 600,
}))

const RemoveCouponChip = styled(Chip)(({ theme }) => ({
  alignSelf: "flex-start",
  backgroundColor: theme.palette.colors.success50,
  color: theme.palette.colors.success700,
  fontWeight: 600,
}))

const BreakdownColumn = styled(Column)(({ theme }) => ({
  border: `1px solid ${theme.palette.colors.gray200}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  gap: theme.spacing(1.5),
}))
